import { connect } from "react-redux";

import { actions as accordionActions, model as accordionModel } from "~/accordion";
import { actions as cdActions, selectors as cdSelectors } from "~/customer-data";
import { getUser, getTheUserLockCustomer } from "~/login/selectors";
import { actions as recsEventsActions, eventsSelectors, models } from "~/recs-events";
import { AgEventSummary } from "~/recs-events/events/model";
import {
    MergeableEvent,
    MergeableApplicationEvents,
    MergeableHarvestEvents,
    MergeablePlantingEvents,
    MergeableSoilEvents,
} from "~/data/mergeable-events";

import { getEventGuidFromDimIdx } from "../../../../../common/accordion/rec-event-accordion-item";

import { ContextMenu } from "../../../../../context-menus/item-selection-context-menu";
import { messages as menuMessages } from "../../../../../context-menus/i18n-messages";

import * as selectors from "../../selectors";
import * as actions from "../../actions";

import * as eventListSelectors from "~/action-panel/components/event-module/components/event-list/selectors";
import * as recListSelectors from "~/action-panel/components/rec-module/components/rec-list/selectors";

import { messages } from "../../../i18n-messages";
import { StatusCodes } from "../../../../../common/status-messages";
import { logFirebaseEvent } from "~/utils/firebase";

const fieldAccordionItemData = (accordionItems) => {
    const root = Array.from(
        accordionModel.getDimIdxSliceIter(accordionItems, [0], undefined, false, [1])
    ).map((dimIdx) => {
        const isExpanded = accordionModel.getItem(accordionItems, dimIdx).expanded;
        return { dimIdx, isExpanded } as {
            dimIdx: number[];
            isExpanded: boolean;
        };
    });

    const someAreExpanded = root.some(({ isExpanded }) => isExpanded);
    const someAreCollapsed = root.some(({ isExpanded }) => !isExpanded);

    return { someAreExpanded, someAreCollapsed };
};

const eventAccordionItemData = (accordionItems: any[], selectedEventGuidSet: Set<string>) => {
    const root = Array.from(
        accordionModel.getDimIdxSliceIter(accordionItems, [0], undefined, false, [2])
    ).map((dimIdx) => {
        const eventGuid = getEventGuidFromDimIdx(accordionItems, dimIdx);
        const isSelected = selectedEventGuidSet.has(eventGuid as string);
        return { dimIdx, isSelected };
    });

    const someAreSelected = root.some(({ isSelected }) => isSelected);
    const someAreDeselected = root.some(({ isSelected }) => !isSelected);
    const selectedDimIdxList = root
        .filter(({ isSelected }) => isSelected)
        .map(({ dimIdx }) => dimIdx);

    return { someAreSelected, someAreDeselected, selectedDimIdxList };
};

const summaryToMergeableEvent = (summary: AgEventSummary): MergeableEvent => ({
    activeYn: summary.activeYn,
    agEventDisplayName: summary.agEventDisplayName,
    agEventGeneralGuid: summary.agEventGeneralGuid,
    agEventGuid: summary.agEventGuid,
    agEventName: summary.agEventName,
    agEventTransactionTypeGuid: summary.agEventTransactionTypeGuid,
    agEventTypeName: summary.agEventTypeName,
    cropDisplayName: summary.cropDisplayName,
    cropGuids: summary.cropGuids,
    croppingSeasonGuid: summary.croppingSeasonGuid,
    croppingSeasonName: summary.croppingSeasonName,
    eventDate: summary.eventDate,
    eventId: summary.eventId,
    fieldGuid: summary.fieldGuid,
    hasSoilSampleResults: summary.hasSoilSampleResults,
    importedPoints: summary.importedPoints,
    importedStatus: summary.importedStatus,
    modifiedDate: summary.modifiedDate,
    numberOfSampleDepths: summary.numberOfSampleDepths,
    status: summary.status,
    totalPoints: summary.totalPoints,
    calculatedArea: summary.calculatedArea,
});

const statusToMerge = [StatusCodes.Complete, StatusCodes.Exported];

const extractMergeableApplicationEvents = (
    selected: AgEventSummary[]
): MergeableApplicationEvents | null => {
    const mergeableEvents = selected
        .filter(
            (event) =>
                event.fieldGuid === selected[0].fieldGuid &&
                event.croppingSeasonGuid === selected[0].croppingSeasonGuid &&
                statusToMerge.includes(event.importedStatus)
        )
        .map(summaryToMergeableEvent);
    return mergeableEvents.length >= 2
        ? new MergeableApplicationEvents(mergeableEvents[0], mergeableEvents.slice(1))
        : null;
};

const extractHarvestMergeableEvents = (
    selected: AgEventSummary[]
): MergeableHarvestEvents | null => {
    const mergeableEvents = selected
        .filter(
            (event) =>
                event.fieldGuid === selected[0].fieldGuid &&
                event.croppingSeasonGuid === selected[0].croppingSeasonGuid &&
                event.cropGuids[0] === selected[0].cropGuids[0] &&
                statusToMerge.includes(event.importedStatus)
        )
        .map(summaryToMergeableEvent);
    return mergeableEvents.length >= 2
        ? new MergeableHarvestEvents(mergeableEvents[0], mergeableEvents.slice(1))
        : null;
};

const extractPlantingMergeableEvents = (
    selected: AgEventSummary[]
): MergeablePlantingEvents | null => {
    const mergeableEvents = selected
        .filter(
            (event) =>
                event.fieldGuid === selected[0].fieldGuid &&
                event.croppingSeasonGuid === selected[0].croppingSeasonGuid &&
                event.cropGuids[0] === selected[0].cropGuids[0] &&
                statusToMerge.includes(event.importedStatus)
        )
        .map(summaryToMergeableEvent);
    return mergeableEvents.length >= 2
        ? new MergeablePlantingEvents(mergeableEvents[0], mergeableEvents.slice(1))
        : null;
};

//Maybe add check for status?
const extractMergeableSoilEvents = (selected: AgEventSummary[]): MergeableSoilEvents | null => {
    const mergeableEvents = selected
        .filter(
            (event) =>
                event.fieldGuid === selected[0].fieldGuid &&
                event.hasSoilSampleResults === selected[0].hasSoilSampleResults &&
                event.numberOfSampleDepths === selected[0].numberOfSampleDepths &&
                statusToMerge.includes(event.importedStatus)
        )
        .map(summaryToMergeableEvent);
    return mergeableEvents.length >= 2
        ? new MergeableSoilEvents(
              mergeableEvents[0],
              mergeableEvents.slice(1),
              mergeableEvents[0].hasSoilSampleResults
          )
        : null;
};

const modifyMenuItems = (menuItems, props) => {
    const { formatMessage } = props.intl;
    const {
        eventTypeOptions,
        getBatchEnabled,
        getCollapseAllArg,
        getCollapseAllEnabled,
        getExpandAllArg,
        getExpandAllEnabled,
        getIsYieldCalibrationEnabled,
        getIsYieldCalibrationInProgress,
        getIsBatchCopyEnabled,
        getIsBatchExportPointsEnabled,
        getIsBatchExportZonesEnabled,
        getIsBatchExportEventDataEnabled,
        getIsBatchExportSurfaceEnabled,
        getIsMergeEventsEnabled,
        getIsExportEventSummaryEnabled,
        getSelectedBatchEditEvents,
        onCollapseAll,
        onExpandAll,
        onNewBatchEvent,
        onCalibrateYield,
        onBatchCopy,
        onBatchExportPoints,
        onBatchExportZones,
        onBatchExportEventData,
        onBatchExportSurface,
        onExportEventSummary,
        onEventSelection,
        onValidateMergeEvents,
        onRecSelection,
        onShowBatchEditEvents,
        eventSelectionCount,
        recSelectionCount,
        sampleSelectionCount,
        userInfo,
    } = props;
    //TODO: Remove scouting check once batch is functional
    const batchEvents = !userInfo.role.batchEvents
        ? []
        : [
              {
                  label: formatMessage(menuMessages.newBatchEventText),
                  disabled: !getBatchEnabled(),
                  subMenuItems: eventTypeOptions
                      .filter(
                          (eventTypeOption) =>
                              eventTypeOption.label !== models.EVENT_TYPE_NAME_SCOUTING
                      )
                      .map((eventTypeOption) => ({
                          key: eventTypeOption.label,
                          label: eventTypeOption.label,
                          action: () => onNewBatchEvent(eventTypeOption.value),
                      })),
              },
          ];

    const expandCollapse = [
        {
            label: formatMessage(menuMessages.expandAll),
            action: () => onExpandAll(getExpandAllArg()),
            disabled: !getExpandAllEnabled(),
        },
        {
            label: formatMessage(menuMessages.collapseAll),
            action: () => onCollapseAll(getCollapseAllArg()),
            disabled: !getCollapseAllEnabled(),
        },
    ];

    const calibrateYield = {
        label: formatMessage(menuMessages.calibrateYield),
        action: () => onCalibrateYield(),
        disabled: getIsYieldCalibrationInProgress() ? true : !getIsYieldCalibrationEnabled(),
    };

    const batchEditEvents = {
        label: formatMessage(menuMessages.batchEditEventText),
        action: () => onShowBatchEditEvents(),
        disabled: getSelectedBatchEditEvents().length == 0,
    };

    const batchExportPoints = {
        label: formatMessage(menuMessages.batchExportPoints),
        action: () => onBatchExportPoints(),
        disabled: !getIsBatchExportPointsEnabled(),
    };

    const batchExportZones = {
        label: formatMessage(menuMessages.batchExportZones),
        action: () => onBatchExportZones(),
        disabled: !getIsBatchExportZonesEnabled(),
    };

    const batchExportEventData = {
        label: formatMessage(menuMessages.batchExportEventData),
        action: () => onBatchExportEventData(),
        disabled: !getIsBatchExportEventDataEnabled(),
    };

    const batchExportSurface = {
        label: formatMessage(menuMessages.batchExportSurface),
        action: () => onBatchExportSurface(),
        disabled: !getIsBatchExportSurfaceEnabled(),
    };

    const mergeEvents = userInfo.role.mergeEvents
        ? {
              label: formatMessage(menuMessages.mergeEventsText),
              action: () => onValidateMergeEvents(),
              disabled: !getIsMergeEventsEnabled(),
          }
        : null;

    const exportEventSummary = {
        label: formatMessage(menuMessages.exportEventSummaryText),
        action: () => onExportEventSummary(),
        disabled: !getIsExportEventSummaryEnabled(),
    };

    const eventRecSelection = [];

    if (recSelectionCount > 0) {
        eventRecSelection.push({
            label: formatMessage(menuMessages.recSelectionText),
            action: () => onRecSelection(),
        });
    }
    if (eventSelectionCount > 0) {
        eventRecSelection.push({
            label: formatMessage(menuMessages.eventSelectionText),
            action: () => onEventSelection(),
        });
    }
    if (sampleSelectionCount > 1 && userInfo.role.batchEvents) {
        eventRecSelection.push({
            label: formatMessage(menuMessages.batchCopy),
            action: () => {
                logFirebaseEvent("sampling_batch_copy");
                onBatchCopy();
            },
            disabled: !getIsBatchCopyEnabled(),
        });
    }
    const newMenuItems = [
        ...batchEvents,
        ...menuItems.slice(0, 2),
        ...eventRecSelection,
        ...expandCollapse,
        ...menuItems.slice(2),
        calibrateYield,
    ];
    if (userInfo.role.batchEditEvents) {
        newMenuItems.push(batchEditEvents);
    }
    if (userInfo.role.exportEventSummary) {
        newMenuItems.push(exportEventSummary);
    }
    if (userInfo.role.exportSamplePoints) {
        newMenuItems.push(batchExportPoints);
    }
    if (userInfo.role.exportSampleZones) {
        newMenuItems.push(batchExportZones);
    }
    if (userInfo.role.exportEventData) {
        newMenuItems.push(batchExportEventData);
    }
    if (userInfo.role.exportSurfaces) {
        newMenuItems.push(batchExportSurface);
    }
    if (mergeEvents) {
        newMenuItems.push(mergeEvents);
    }

    return newMenuItems;
};

const mapStateToProps = (state) => {
    const accordionState = selectors.getAccordionState(state);
    const eventTypeOptions = eventsSelectors.getNewableEventTypeOptions(state);
    const { accordionId } = accordionState;
    const accordionItems = accordionState.items;
    const selectedEventGuidSet: Set<string> = selectors.getSelectedEventGuidSet(state);

    const agEventSummaryMap = eventsSelectors.getEventGeneralGuidToEventSummaryMap(state);

    const selectedSurfaceEvents = Array.from(selectedEventGuidSet).reduce((accumulator, guid) => {
        const selectedEvent = agEventSummaryMap.get(guid);
        if (
            selectedEvent != null &&
            !(
                (selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_SOIL ||
                    selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE ||
                    selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_SCOUTING) &&
                selectedEvent.importedPoints === 0
            )
        ) {
            accumulator.push(selectedEvent);
        }

        return accumulator;
    }, []);

    const selectedImportedEvents = Array.from(selectedEventGuidSet).reduce((accumulator, guid) => {
        const selectedEvent = agEventSummaryMap.get(guid);
        if (
            selectedEvent != null &&
            !(
                selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_SOIL ||
                selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE ||
                selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_SCOUTING ||
                selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_IRRIGATION ||
                selectedEvent.agEventTypeName === models.EVENT_TYPE_NAME_TILLAGE
            ) &&
            selectedEvent.isImportedYn
        ) {
            accumulator.push(selectedEvent);
        }

        return accumulator;
    }, []);

    const selectedBatchEditEvents = Array.from(selectedEventGuidSet).reduce((accumulator, guid) => {
        const selectedEvent = agEventSummaryMap.get(guid);
        if (
            selectedEvent != null &&
            (selectedEvent.agEventTypeName.startsWith(models.EVENT_TYPE_NAME_APPLICATION) ||
                selectedEvent.agEventTypeName.startsWith(models.EVENT_TYPE_NAME_HARVEST) ||
                selectedEvent.agEventTypeName.startsWith(models.EVENT_TYPE_NAME_PLANTING)) &&
            selectedEvent.isImportedYn
        ) {
            accumulator.push(selectedEvent);
        }

        return accumulator;
    }, []);

    const getFieldGuidInfo = () => {
        const fieldGuidList = accordionItems.map(
            (accordionItem) => accordionItem.payload.fieldGuid
        );
        const fieldGuidToBoundaryGuidMap = new Map(
            accordionItems.map((accordionItem) => [
                accordionItem.payload.fieldGuid,
                accordionItem.payload.fieldBoundaryGuid,
            ])
        );
        const fieldGuidToCustomerGuidMap = new Map(
            accordionItems.map((accordionItem) => [
                accordionItem.payload.fieldGuid,
                accordionItem.payload.customerGuid,
            ])
        );

        return [fieldGuidList, fieldGuidToBoundaryGuidMap, fieldGuidToCustomerGuidMap];
    };

    const selectedSummaries = Array.from(selectedEventGuidSet).map((agEventGeneralGuid) =>
        agEventSummaryMap.get(agEventGeneralGuid)
    );
    const nonNullSelectedSummaries = selectedSummaries.filter((summary) => summary != null);

    const selectedApplicationEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_APPLICATION
    );
    const selectedHarvestEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_HARVEST
    );
    const selectedEcDataEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_EC_DATA
    );
    const selectedPlantingEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_PLANTING
    );
    const selectedPlantingApplicationEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_PLANTING_APPLICATION
    );
    const selectedSoilEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_SOIL
    );
    const selectedNematodeEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_NEMATODE
    );
    const selectedTissueEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE
    );
    const selectedSampleEvents = nonNullSelectedSummaries.filter(
        (summary) =>
            summary.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_SOIL ||
            summary.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE ||
            summary.agEventTypeName === models.EVENT_TYPE_NAME_SAMPLING_NEMATODE
    );

    const selectedCompletedEvents = nonNullSelectedSummaries.filter(
        (summary) => summary.importedStatus === StatusCodes.Complete
    );

    const isImportedSummary = (summary) => summary.isImportedYn;
    const selectedImportedApplicationEvents = selectedApplicationEvents.filter(isImportedSummary);
    const selectedImportedHarvestEvents = selectedHarvestEvents.filter(isImportedSummary);
    const selectedImportedEcDataEvents = selectedEcDataEvents.filter(isImportedSummary);
    const selectedImportedPlantingEvents = selectedPlantingEvents.filter(isImportedSummary);
    const selectedImportedSampleEvents = selectedSampleEvents.filter(isImportedSummary);

    const selectedImportedAggregateEvents =
        selectedPlantingApplicationEvents.filter(isImportedSummary);
    const allAreSoilSampleEvents = selectedSoilEvents.length === nonNullSelectedSummaries.length;
    const allAreTissueSampleEvents =
        selectedTissueEvents.length === nonNullSelectedSummaries.length;
    const someAreCompletedEvents = selectedCompletedEvents.length > 0;
    const someProcessingOrErrorSampling = selectedSampleEvents.some(
        (s) =>
            s.importedStatus === StatusCodes.CreatingEvent ||
            s.importedStatus === StatusCodes.ErrorCreatingEvent
    );
    const someZoneSamplingSampleEvents = selectedSampleEvents.some(
        (summary) => summary.interpolationType === "Zone Sampling"
    );

    const someAreImportedHarvestEvents = selectedImportedHarvestEvents.length > 0;
    const someAreImportedSampleEvents = selectedImportedSampleEvents.length > 0;
    const isAllBatchYieldCalibrationProcessing = selectedImportedHarvestEvents.some(
        (x) =>
            x.importedStatus === StatusCodes.EventProcessing ||
            x.importedStatus === StatusCodes.ProcessingSurfaces ||
            x.importedStatus === StatusCodes.WaitingToProcess
    );

    const someAreImportedNonSoilEvents =
        selectedImportedApplicationEvents.length +
            selectedImportedHarvestEvents.length +
            selectedImportedEcDataEvents.length +
            selectedImportedPlantingEvents.length >
        0;

    const potentialMergeableEvents = [
        extractMergeableApplicationEvents(selectedImportedApplicationEvents),
        extractMergeableSoilEvents(selectedSoilEvents),
        extractMergeableSoilEvents(selectedNematodeEvents),
        extractHarvestMergeableEvents(selectedImportedHarvestEvents),
        extractPlantingMergeableEvents([
            ...selectedImportedPlantingEvents,
            ...selectedImportedAggregateEvents,
        ]),
    ]
        .filter((x) => x)
        .filter((x) => x.all.length > 0);

    const mergeableEvents =
        potentialMergeableEvents.length === 1 ? potentialMergeableEvents[0] : null;
    const fieldGuids = [];
    let multipleEventsPerField = false;
    selectedSampleEvents.forEach((e) => {
        if (fieldGuids.includes(e.fieldGuid)) {
            multipleEventsPerField = true;
        } else {
            fieldGuids.push(e.fieldGuid);
        }
    });
    const { someAreExpanded, someAreCollapsed } = fieldAccordionItemData(accordionItems);
    const { someAreSelected, someAreDeselected, selectedDimIdxList } = eventAccordionItemData(
        accordionItems,
        selectedEventGuidSet
    );
    const eventGeneralGuidToEventMap = eventsSelectors.getEventGeneralGuidToEventSummaryMap(state);
    const selectedFieldGuids = cdSelectors.getSelectedFieldGuids(state);
    const lockCustomersNotEnrolled = getTheUserLockCustomer(state);

    const isLocked =
        lockCustomersNotEnrolled &&
        selectedFieldGuids.count() > 0 &&
        selectedFieldGuids.count() > 0 &&
        Array.from(eventGeneralGuidToEventMap.values()).some(
            (e) => selectedFieldGuids.has(e.fieldGuid) && !e.customerEnrolled
        );

    return {
        deleteConfirmationMessage: messages.deleteMultipleEventsConfirmation,
        eventTypeOptions,
        getBatchEnabled: () => accordionItems.length > 1 && !isLocked,
        getClearSelectedArg: () => null,
        getClearSelectedEnabled: () => someAreSelected,
        getCollapseAllArg: () => accordionId,
        getCollapseAllEnabled: () => someAreExpanded,
        getDeleteSelectedArg: () => selectedDimIdxList,
        getDeleteSelectedEnabled: () => someAreSelected,
        getExpandAllArg: () => accordionId,
        getExpandAllEnabled: () => someAreCollapsed,
        getIsYieldCalibrationEnabled: () => someAreImportedHarvestEvents,
        getIsYieldCalibrationInProgress: () => isAllBatchYieldCalibrationProcessing,
        getIsBatchCopyEnabled: () =>
            selectedSampleEvents.length > 1 &&
            !multipleEventsPerField &&
            !someProcessingOrErrorSampling &&
            (allAreSoilSampleEvents || allAreTissueSampleEvents),
        getIsBatchExportPointsEnabled: () => someAreImportedSampleEvents,
        getIsBatchExportZonesEnabled: () => someZoneSamplingSampleEvents,
        getIsBatchExportEventDataEnabled: () => someAreImportedNonSoilEvents,
        getIsBatchExportSurfaceEnabled: () => someAreCompletedEvents,
        getIsExportEventSummaryEnabled: () => accordionItems.length > 0,
        getIsMergeEventsEnabled: () => mergeableEvents !== null,
        getFieldGuidInfo,
        getSelectAllArg: () => null,
        getSelectAllEnabled: () => someAreDeselected,
        getSelectedBatchEditEvents: () => selectedBatchEditEvents,
        getSelectedHarvestAgEventGeneralGuids: () =>
            selectedImportedHarvestEvents.map((aeg) => aeg.agEventGeneralGuid),
        getSelectedSoilAgEvents: () => selectedSoilEvents,
        getSelectedCompletedEvents: () => selectedCompletedEvents,
        getSelectedSampleAgEvents: () => selectedSampleEvents,
        getSelectedEvents: () => selectedImportedEvents,
        getSelectedSurfaceEvents: () => selectedSurfaceEvents,
        getMergeableEvents: () => mergeableEvents,
        modifyMenuItems,
        eventSelectionCount: eventListSelectors.getSelectedEventGuidSet(state).size,
        recSelectionCount: recListSelectors.getSelectedRecGuidSet(state).size,
        sampleSelectionCount: selectedSampleEvents.length,
        selectedEventFieldGuidSet: eventListSelectors.getSelectedEventFieldGuidSet(state),
        selectedRecFieldGuidSet: recListSelectors.getSelectedRecFieldGuidSet(state),
        selectedFieldGuids: cdSelectors.getSelectedFieldGuids(state),
        userInfo: getUser(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        clearSelectedFields: (fieldGuids) => dispatch(cdActions.clearSelectedFields(fieldGuids)),
        selectFields: (fieldGuidList) => dispatch(cdActions.addSelectedFields(fieldGuidList)),
        onSelectAll: () => dispatch(actions.selectEventsFromAccordion([0], null)),
        onClearSelected: () => dispatch(actions.deselectEventsFromAccordion([0], null)),
        onDeleteSelected: (toDeleteDimIdxList) =>
            dispatch(actions.deleteEventsFromDimIdxList(toDeleteDimIdxList)),
        onExpandAll: (accordionId) =>
            dispatch(accordionActions.expandAllAccordionItems(accordionId, [1])),
        onCollapseAll: (accordionId) =>
            dispatch(accordionActions.collapseAllAccordionItems(accordionId, [1])),
        onNewBatchEvent: (
            fieldGuidList,
            fieldGuidToBoundaryGuidMap,
            fieldGuidToCustomerGuidMap,
            agEventTransactionTypeName
        ) =>
            dispatch(
                recsEventsActions.createNewBatchEvent(
                    fieldGuidList,
                    fieldGuidToBoundaryGuidMap,
                    fieldGuidToCustomerGuidMap,
                    agEventTransactionTypeName
                )
            ),
        onCalibrateYield: (agEventGeneralGuidList) =>
            dispatch(actions.loadYieldCalibration(agEventGeneralGuidList)),
        onBatchCopy: (selectedAgEvents, fieldGuidList, fieldGuidToBoundaryGuidMap) =>
            dispatch(
                recsEventsActions.batchCopyEvents(
                    selectedAgEvents,
                    fieldGuidList,
                    fieldGuidToBoundaryGuidMap
                )
            ),
        onBatchExportPoints: (selectedAgEvents) =>
            dispatch(actions.exportSamplingPoints(selectedAgEvents)),
        onBatchExportZones: (selectedAgEvents) =>
            dispatch(actions.exportSamplingZones(selectedAgEvents)),
        onBatchExportEventData: (selectedImportedEvents) =>
            dispatch(actions.exportEventData(selectedImportedEvents)),
        onBatchExportSurface: (selectedCompletedEvents) =>
            dispatch(actions.exportSurfaceData(selectedCompletedEvents)),
        onValidateMergeEvents: (mergeableEvents) =>
            dispatch(recsEventsActions.validateEventsMerge(mergeableEvents)),
        onMergeEvents: () => dispatch(actions.showMergeModal()),
        onExportEventSummary: () => dispatch(actions.toggleExportEventSummaryModal(true)),
        onShowBatchEditEvents: () => dispatch(actions.toggleBatchEditEventsModal(true)),
        setMergeableEvents: (mergeableEvents) =>
            dispatch(actions.setMergeableEvents(mergeableEvents)),
    };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    onNewBatchEvent: (eventTypeInfo) => {
        const { agEventTransactionTypeName } = eventTypeInfo;
        const [fieldGuidList, fieldGuidToBoundaryGuidMap, fieldGuidToCustomerGuidMap] =
            stateProps.getFieldGuidInfo();
        dispatchProps.onNewBatchEvent(
            fieldGuidList,
            fieldGuidToBoundaryGuidMap,
            fieldGuidToCustomerGuidMap,
            agEventTransactionTypeName
        );
    },
    onCalibrateYield: () => {
        dispatchProps.onCalibrateYield(stateProps.getSelectedHarvestAgEventGeneralGuids());
    },
    onBatchCopy: () => {
        const [fieldGuidList, fieldGuidToBoundaryGuidMap] = stateProps.getFieldGuidInfo();
        dispatchProps.onBatchCopy(
            stateProps.getSelectedSampleAgEvents(),
            fieldGuidList,
            fieldGuidToBoundaryGuidMap
        );
    },
    onBatchExportPoints: () => {
        dispatchProps.onBatchExportPoints(stateProps.getSelectedSampleAgEvents());
    },
    onBatchExportZones: () => {
        dispatchProps.onBatchExportZones(stateProps.getSelectedSampleAgEvents());
    },
    onBatchExportEventData: () => {
        dispatchProps.onBatchExportEventData(stateProps.getSelectedEvents());
    },
    onBatchExportSurface: () => {
        dispatchProps.onBatchExportSurface(stateProps.getSelectedSurfaceEvents());
    },
    onExportEventSummary: () => {
        dispatchProps.onExportEventSummary();
    },

    onEventSelection: () => {
        const { selectedFieldGuids, selectedEventFieldGuidSet } = stateProps;
        const { clearSelectedFields, selectFields } = dispatchProps;

        logFirebaseEvent("event_selection");
        if (selectedEventFieldGuidSet.size > 0) {
            clearSelectedFields(Array.from(selectedFieldGuids.subtract(selectedEventFieldGuidSet)));
            selectFields(Array.from(selectedEventFieldGuidSet.values()));
        }
    },
    onValidateMergeEvents: () => {
        const mergeableEvents = stateProps.getMergeableEvents();
        dispatchProps.setMergeableEvents(mergeableEvents);
        dispatchProps.onValidateMergeEvents(mergeableEvents);
    },
    onRecSelection: () => {
        const { selectedFieldGuids, selectedRecFieldGuidSet } = stateProps;
        const { clearSelectedFields, selectFields } = dispatchProps;

        logFirebaseEvent("rec_selection");
        if (selectedRecFieldGuidSet.size > 0) {
            clearSelectedFields(Array.from(selectedFieldGuids.subtract(selectedRecFieldGuidSet)));
            selectFields(Array.from(selectedRecFieldGuidSet.values()));
        }
    },
});

export const TabsContextMenu = connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(ContextMenu);
